import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExam } from "../context/ExamContext";
import {
  getExams,
  startExamAttempt,
  getAttemptQuestions,
  saveAnswer,
  submitExamSection,
  submitExam,
} from "../api/client";

import BasicCalculator from "./BasicCalculator";
import Whiteboard from "./Whiteboard";
import FactorTables from "./FactorTables";
import ProctorCamera from "./Proctorcamera";

const CMA_MCQ_WEIGHT = 90;

function formatTime(totalSeconds) {
  const safe = Math.max(0, Number(totalSeconds) || 0);
  const h = String(Math.floor(safe / 3600)).padStart(2, "0");
  const m = String(Math.floor((safe % 3600) / 60)).padStart(2, "0");
  const s = String(safe % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function getSecondsUntil(dateString) {
  if (!dateString) return 0;
  const target = new Date(dateString).getTime();
  if (Number.isNaN(target)) return 0;
  return Math.max(0, Math.floor((target - Date.now()) / 1000));
}

function normalizeQuestion(item) {
  return {
    ...item,
    id: item.id,
    question_text: item.question_text || item.question || item.text || "",
    question_type: item.question_type || item.question_format || "MCQ",
    question_format: item.question_format || item.question_type || "MCQ",
    options: Array.isArray(item.options) ? item.options : [],
  };
}

function isAnswered(answer) {
  if (!answer) return false;
  if (
    answer.selected_option_id !== undefined &&
    answer.selected_option_id !== null
  ) {
    return true;
  }

  if (
    Array.isArray(answer.selected_option_ids) &&
    answer.selected_option_ids.length > 0
  ) {
    return true;
  }

  if (
    typeof answer.essay_answer === "string" &&
    answer.essay_answer.trim()
  ) {
    return true;
  }

  return false;
}

function Shell({ children }) {
  return (
    <div className="oes-page-full">
      <div className="oes-page container p-4">{children}</div>
    </div>
  );
}

export default function Examination() {
  const navigate = useNavigate();
  const { selectedCourse, selectedExam, setLastResult } = useExam();
  const [exam, setExam] = useState(selectedExam || null);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState({});
  const [current, setCurrent] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showTables, setShowTables] = useState(false);
  
  const submittedRef = useRef(false);
  const mountedRef = useRef(true);
  const loadStartedRef = useRef(false);
  const loadKeyRef = useRef("");
  const submittingRef = useRef(false);
  const finalSubmitRef = useRef(null);

  const loadExam = async () => {
    if (!selectedCourse?.id && !selectedExam?.id) {
      setError("No examination was selected.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      let matchingExam = selectedExam || null;
      if (!matchingExam && selectedCourse?.id) {
        const response = await getExams();
        const raw = response?.data;
        const exams = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.results)
          ? raw.results
          : [];
        matchingExam = exams.find(
          (item) =>
            Number(item.course) === Number(selectedCourse.id) ||
            Number(item.course_id) === Number(selectedCourse.id)
        );
      }

      if (!matchingExam) {
        throw new Error(
          `No active exam is configured for ${
            selectedCourse?.name || "this course"
          }.`
        );
      }

      if (!mountedRef.current) return;
      setExam(matchingExam);

      const startResponse = await startExamAttempt(matchingExam.id);
      const attemptData = startResponse?.data || {};

      if (!attemptData.attempt_id) {
        throw new Error("The backend did not return an attempt ID.");
      }

      if (!mountedRef.current) return;
      setAttempt(attemptData);

      const questionResponse = await getAttemptQuestions(attemptData.attempt_id);
      const questionData = questionResponse?.data || {};
      const backendQuestions = Array.isArray(questionData)
        ? questionData
        : Array.isArray(questionData.questions)
        ? questionData.questions
        : [];

      const normalizedQuestions = backendQuestions.map(normalizeQuestion);

      if (!mountedRef.current) return;
      setQuestions(normalizedQuestions);

      const endTime = questionData.ends_at || attemptData.ends_at || null;
      setSecondsLeft(getSecondsUntil(endTime));

      const restoredAnswers = {};
      normalizedQuestions.forEach((q) => {
        if (q.answer) {
          restoredAnswers[q.id] = q.answer;
        }
        if (q.selected_option_id) {
          restoredAnswers[q.id] = {
            selected_option_id: q.selected_option_id,
          };
        }
        if (q.essay_answer) {
          restoredAnswers[q.id] = {
            essay_answer: q.essay_answer,
          };
        }
      });
      setAnswers(restoredAnswers);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to start the examination.";
      if (mountedRef.current) {
        setError(detail);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  /* ----------------------------------------------------------------
     TIMER (auto submit when it reaches zero)
  ---------------------------------------------------------------- */
  useEffect(() => {
    if (loading || !attempt || submittedRef.current) {
      return undefined;
    }

    const timer = setInterval(() => {
      setSecondsLeft((value) => {
        if (value <= 1) {
          clearInterval(timer);
          if (!submittedRef.current && !submittingRef.current) {
            finalSubmitRef.current?.(true);
          }
          return 0;
        }
        return value - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loading, attempt, exam, selectedCourse, navigate, setLastResult]);

  useEffect(() => {
    mountedRef.current = true;
    const loadKey = `${selectedCourse?.id || ""}-${selectedExam?.id || ""}`;

    if (loadStartedRef.current && loadKeyRef.current === loadKey) {
      return () => {
        mountedRef.current = false;
      };
    }

    loadStartedRef.current = true;
    loadKeyRef.current = loadKey;
    loadExam();

    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse?.id, selectedExam?.id]);

  /* ----------------------------------------------------------------
     QUESTIONS & NAVIGATION
  ---------------------------------------------------------------- */
  const total = questions.length;
  const question = questions[current] || null;

  const goToQuestion = (index) => {
    if (index < 0 || index >= total) return;
    setCurrent(index);
  };

  /* ----------------------------------------------------------------
     ANSWERS & COMPUTED PROGRESS
  ---------------------------------------------------------------- */
  const answeredCount = useMemo(
    () => Object.values(answers).filter(isAnswered).length,
    [answers]
  );

  const completionPercent = useMemo(() => {
    if (total === 0) return 0;

    const examLabel = exam?.name || selectedCourse?.name || "";
    const isCMA = /\bCMA\b/i.test(examLabel);
    const firstEssay = questions.findIndex(
      (item) => String(item.question_type || "").toUpperCase() === "ESSAY"
    );

    if (isCMA && firstEssay > 0) {
      if (current < firstEssay) {
        return Math.round(((current + 1) / firstEssay) * CMA_MCQ_WEIGHT);
      }

      const essayTotal = total - firstEssay;
      const essayIndex = current - firstEssay + 1;

      return Math.round(
        CMA_MCQ_WEIGHT + (essayIndex / essayTotal) * (100 - CMA_MCQ_WEIGHT)
      );
    }

    return Math.round(((current + 1) / total) * 100);
  }, [current, total, questions, exam, selectedCourse]);

  const saveCurrentAnswer = async (nextAnswer, questionOverride = null) => {
    const targetQuestion = questionOverride || question;
    if (!attempt || !targetQuestion) {
      return false;
    }

    setSaving(true);
    setError("");

    try {
      await saveAnswer(attempt.attempt_id, targetQuestion.id, nextAnswer);
      return true;
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Could not save this answer.";

      if (mountedRef.current) {
        setError(detail);
      }
      return false;
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  };

  const selectMCQ = async (optionId) => {
    if (!question) return;

    const next = { selected_option_id: optionId };
    setAnswers((previous) => ({
      ...previous,
      [question.id]: next,
    }));

    await saveCurrentAnswer(next);
  };

  const selectMultiple = async (optionId) => {
    if (!question) return;

    const existing = answers[question.id]?.selected_option_ids || [];
    const exists = existing.includes(optionId);

    const nextIds = exists
      ? existing.filter((id) => id !== optionId)
      : [...existing, optionId];

    setAnswers((previous) => ({
      ...previous,
      [question.id]: { selected_option_ids: nextIds },
    }));

    await saveCurrentAnswer({
      answer_data: {
        selected_options: nextIds,
      },
    });
  };

  const setEssayAnswer = (value) => {
    if (!question) return;

    setAnswers((previous) => ({
      ...previous,
      [question.id]: { essay_answer: value },
    }));
  };

  const saveEssayAnswer = async () => {
    if (!question) return;
    const existing = answers[question.id] || { essay_answer: "" };

    if (!String(existing.essay_answer || "").trim()) {
      return;
    }

    await saveCurrentAnswer(existing);
  };

  const toggleMark = () => {
    if (!question) return;
    setMarked((previous) => ({
      ...previous,
      [question.id]: !previous[question.id],
    }));
  };

  /* ----------------------------------------------------------------
     SECTION SUBMISSION
  ---------------------------------------------------------------- */
  const saveEssayAnswerIfNeeded = async () => {
    if (
      !question ||
      String(question.question_type || "").toUpperCase() !== "ESSAY"
    ) {
      return;
    }

    const existing = answers[question.id];
    if (!existing) return;
    if (!String(existing.essay_answer || "").trim()) return;

    await saveCurrentAnswer(existing);
  };

  const reloadCurrentAttemptQuestions = async (attemptId) => {
    const response = await getAttemptQuestions(attemptId);
    const data = response?.data || {};

    const rawQuestions = Array.isArray(data)
      ? data
      : Array.isArray(data.questions)
      ? data.questions
      : [];

    setQuestions(rawQuestions.map(normalizeQuestion));
    setCurrent(0);

    if (data.ends_at) {
      setSecondsLeft(getSecondsUntil(data.ends_at));
    }

    return data;
  };

  const handleSectionSubmit = async () => {
    if (!attempt || !attempt.current_section_id) {
      return null;
    }

    await saveEssayAnswerIfNeeded();

    const response = await submitExamSection(
      attempt.attempt_id,
      attempt.current_section_id
    );

    const data = response?.data || {};

    if (data.next_section_available) {
      await reloadCurrentAttemptQuestions(attempt.attempt_id);

      setAttempt((previous) => ({
        ...(previous || {}),
        ...data,
        current_section_id:
          data.next_section_id || previous?.current_section_id,
        ends_at: data.ends_at || previous?.ends_at,
      }));
    }

    return data;
  };

  /* ----------------------------------------------------------------
     FINAL SUBMISSION
  ---------------------------------------------------------------- */
  const performFinalSubmit = async (auto = false) => {
    await saveEssayAnswerIfNeeded();

    const response = await submitExam(attempt.attempt_id);
    const data = response?.data || {};
    const backendResult = data.result || {};

    setLastResult({
      attemptId: attempt.attempt_id,
      examId: exam?.id,
      examName: exam?.name || selectedCourse?.name,
      score: backendResult.score,
      total: backendResult.total_questions,
      correct: backendResult.correct_answers,
      wrong: backendResult.wrong_answers,
      unanswered: backendResult.unanswered_questions,
      result: backendResult.status,
      percentage: backendResult.percentage,
      details: backendResult.details,
      autoSubmitted: auto,
    });

    navigate("/submitted");
  };

  const isAlreadySubmittedError = (err) => {
    const message = String(
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.message ||
      ""
    ).toLowerCase();

    return (
      message.includes("already been submitted") ||
      message.includes("already submitted") ||
      message.includes("not in progress")
    );
  };

  const finishAlreadySubmittedAttempt = (auto = true) => {
    submittedRef.current = true;
    submittingRef.current = false;
    setSubmitting(false);
    setError("");

    setLastResult((previous) => ({
      ...(previous || {}),
      attemptId: attempt?.attempt_id,
      examId: exam?.id,
      examName: exam?.name || selectedCourse?.name,
      autoSubmitted: auto,
      result: previous?.result || "SUBMITTED",
    }));

    navigate("/submitted", { replace: true });
  };

  const handleFinalSubmit = async (auto = false) => {
    if (submittedRef.current || submittingRef.current || !attempt) {
      return;
    }

    submittedRef.current = true;
    submittingRef.current = true;
    setSubmitting(true);
    setError("");

    try {
      await performFinalSubmit(auto);
    } catch (err) {
      console.error("FINAL SUBMIT ERROR:", err?.response?.data || err);

      if (isAlreadySubmittedError(err)) {
        finishAlreadySubmittedAttempt(auto);
        return;
      }

      submittedRef.current = false;
      submittingRef.current = false;
      setSubmitting(false);

      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "The examination could not be submitted."
      );
    }
  };

  finalSubmitRef.current = handleFinalSubmit;

  const handleSubmit = async (auto = false) => {
    if (submittedRef.current || submittingRef.current || !attempt) {
      return;
    }

    const sections = Array.isArray(exam?.sections) ? exam.sections : [];

    if (attempt.current_section_id && sections.length > 1) {
      submittingRef.current = true;
      setSubmitting(true);
      setError("");

      try {
        const result = await handleSectionSubmit();

        if (result?.next_section_available) {
          return;
        }

        submittedRef.current = true;
        await performFinalSubmit(auto);
      } catch (err) {
        console.error("SECTION/FINAL SUBMIT ERROR:", err?.response?.data || err);

        if (isAlreadySubmittedError(err)) {
          finishAlreadySubmittedAttempt(auto);
          return;
        }

        submittedRef.current = false;
        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.message ||
            "Could not submit the section."
        );
      } finally {
        if (!submittedRef.current) {
          submittingRef.current = false;
          setSubmitting(false);
        }
      }

      return;
    }

    await handleFinalSubmit(auto);
  };

  /* ----------------------------------------------------------------
     EARLY STATES
  ---------------------------------------------------------------- */
  if (!selectedCourse && !selectedExam) {
    return (
      <Shell>
        <div className="alert alert-danger">No examination was selected.</div>
        <button className="btn btn-primary" onClick={() => navigate("/home")}>
          Back to Home
        </button>
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="text-muted mt-3">Starting your examination...</p>
        </div>
      </Shell>
    );
  }

  if (error && !question) {
    return (
      <Shell>
        <div className="alert alert-danger">
          <strong>Unable to start examination</strong>
          <div className="mt-2">{error}</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate("/home")}>
          Back to Home
        </button>
      </Shell>
    );
  }

  if (!question) {
    return (
      <Shell>
        <div className="alert alert-danger">
          No questions were assigned to this attempt.
        </div>
      </Shell>
    );
  }

  /* ----------------------------------------------------------------
     RENDER EXAMINATION INTERFACE
  ---------------------------------------------------------------- */
  const questionType = String(
    question.question_type || question.question_format || "MCQ"
  ).toUpperCase();

  const isEssay = questionType === "ESSAY";

  const isMulti =
    questionType === "MULTI_SELECT" ||
    String(question.question_format || "").toUpperCase() === "MULTI_SELECT";

  const answer = answers[question.id] || {};
  const selectedIds = answer.selected_option_ids || [];

  return (
    <div className="oes-page-full">
      <div className="oes-page container-fluid px-2 px-md-4">
        <div className="mb-3" />

        {/* HEADER */}
        <div className="oes-card p-2 p-md-3 mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span className="fw-semibold">
            Exam : {exam?.name || selectedCourse?.name || "Examination"}
          </span>

          <span
            className={`fw-bold ${
              secondsLeft < 300 ? "text-danger" : "text-success"
            }`}
          >
            Time Left {formatTime(secondsLeft)}
          </span>

          <span
            className="btn btn-outline-primary btn-sm"
            style={{ cursor: "default" }}
          >
            <i className="bi bi-bar-chart-line me-1" />
            Completion {completionPercent}%
          </span>

          <span className="text-muted">
            Question {current + 1} / {total}
          </span>
        </div>

        {error && <div className="alert alert-danger py-2">{error}</div>}

        <div className="row g-3">
          {/* QUESTION NAVIGATION GRID */}
          <div className="col-12 col-lg-2 order-lg-1">
            <div className="oes-card p-3 h-100 d-flex flex-column">
              <h2 className="h6 fw-bold mb-2">Questions</h2>

              <div className="question-nav-grid">
                {questions.map((item, index) => {
                  const classes = [
                    "question-nav-btn",
                    isAnswered(answers[item.id]) ? "answered" : "",
                    marked[item.id] ? "marked" : "",
                    index === current ? "current" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <button
                      key={item.id}
                      className={classes}
                      onClick={() => goToQuestion(index)}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* QUESTION AREA */}
          <div className="col-12 col-lg-10 order-lg-2">
            <div className="oes-card p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="h6 fw-bold mb-0">
                  Question {current + 1}
                  {isEssay && (
                    <span className="badge bg-secondary ms-2">Essay</span>
                  )}
                  {isMulti && (
                    <span className="badge bg-secondary ms-2">
                      Multiple Select
                    </span>
                  )}
                </h2>

                <button
                  className="btn btn-sm btn-outline-warning"
                  onClick={toggleMark}
                >
                  <i className="bi bi-bookmark-star me-1" />
                  {marked[question.id] ? "Marked" : "Mark"}
                </button>
              </div>

              <p className="mb-4">{question.question_text}</p>

              {/* ESSAY ANSWER INPUT */}
              {isEssay ? (
                <textarea
                  className="form-control mb-4"
                  rows={10}
                  placeholder="Write your answer here..."
                  value={answer.essay_answer || ""}
                  onChange={(event) => setEssayAnswer(event.target.value)}
                  onBlur={saveEssayAnswer}
                />
              ) : (
                /* MCQ & MULTI SELECT INPUTS */
                <div className="d-flex flex-column gap-2 mb-4">
                  {(question.options || []).map((option) => {
                    const selected = isMulti
                      ? selectedIds.includes(option.id)
                      : Number(answer.selected_option_id) ===
                        Number(option.id);

                    return (
                      <label
                        key={option.id}
                        className={`form-check option-row ${
                          selected ? "selected" : ""
                        }`}
                      >
                        <input
                          className="form-check-input option-radio"
                          type={isMulti ? "checkbox" : "radio"}
                          name={`question-${question.id}`}
                          checked={selected}
                          onChange={() => {
                            if (isMulti) {
                              selectMultiple(option.id);
                            } else {
                              selectMCQ(option.id);
                            }
                          }}
                        />

                        <span className="option-text">
                          {String.fromCharCode(
                            64 + Number(option.option_order || 1)
                          )}
                          . {option.option_text}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {saving && (
                <div className="text-muted small mb-2">Saving answer...</div>
              )}

              {/* PREVIOUS / NEXT NAVIGATION */}
              <div className="d-flex flex-wrap gap-2 justify-content-between">
                <button
                  className="btn btn-outline-primary"
                  disabled={current === 0}
                  onClick={() => goToQuestion(current - 1)}
                >
                  <i className="bi bi-chevron-left me-1" />
                  Previous
                </button>

                <button
                  className="btn btn-outline-primary"
                  disabled={current === total - 1}
                  onClick={() => goToQuestion(current + 1)}
                >
                  Next
                  <i className="bi bi-chevron-right ms-1" />
                </button>
              </div>

              <hr />

              {/* AUXILIARY TOOLS */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowCalculator(true)}
                >
                  <i className="bi bi-calculator me-1" />
                  Calculator
                </button>

                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowWhiteboard(true)}
                >
                  <i className="bi bi-pencil-square me-1" />
                  Whiteboard
                </button>

                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowTables(true)}
                >
                  <i className="bi bi-table me-1" />
                  PV / FV Tables
                </button>

                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowCamera((value) => !value)}
                >
                  <i className="bi bi-camera-video me-1" />
                  {showCamera ? "Hide Camera" : "Show Camera"}
                </button>
              </div>

              <div className="small text-muted mb-2">
                Answered {answeredCount} of {total}
              </div>

              {/* SUBMIT BUTTON */}
              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-danger btn-sm px-4"
                  disabled={submitting}
                  onClick={() => setShowConfirm(true)}
                >
                  {submitting ? "SUBMITTING..." : "SUBMIT EXAM"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PROCTOR CAMERA */}
      {attempt?.attempt_id && (
        <ProctorCamera
          key={`standard-${attempt.attempt_id}`}
          attemptId={attempt.attempt_id}
          attemptType="standard"
          proctoringEnabled={attempt.proctoring_enabled !== false}
          showCameraPreview={showCamera}
          onAutoSubmit={() => handleFinalSubmit(true)}
        />
      )}

      {/* CALCULATOR MODAL */}
      {showCalculator && (
        <BasicCalculator onClose={() => setShowCalculator(false)} />
      )}

      {/* PV / FV FACTOR TABLES MODAL */}
      {showTables && <FactorTables onClose={() => setShowTables(false)} />}

      {/* WHITEBOARD MODAL */}
      {showWhiteboard && (
        <Whiteboard
          open={showWhiteboard}
          onClose={() => setShowWhiteboard(false)}
          resetKey={attempt?.attempt_id}
        />
      )}

      {/* SUBMIT CONFIRMATION MODAL */}
      {showConfirm && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{ background: "rgba(0,0,0,0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Submit Examination?</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowConfirm(false)}
                />
              </div>

              <div className="modal-body">
                <p className="mb-1">Are you sure you want to submit?</p>
                <p className="small text-muted mb-0">
                  Answered {answeredCount} of {total} questions. This cannot be
                  undone.
                </p>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowConfirm(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-danger"
                  disabled={submitting}
                  onClick={() => {
                    setShowConfirm(false);
                    handleSubmit(false);
                  }}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}